import Storages from 'storages';
import StorageKeys from 'enum/storages.enum';
import DeviceTypes from 'enum/device-type.enum';
import { shouldUseTizenTEPPlayer } from 'utils/device/tizenPlatform';
import { getPlayerState } from './channelPlayerState';

/**
 * Device-specific playback control utilities
 * Abstracts Samsung, Tizen, webOS, and Android device player initialization
 */
export const createDevicePlaybackService = () => {

  /**
   * Get current device type
   */
  const getDeviceType = () => {
    const deviceList = Storages.MainStorage.getItem(StorageKeys.Keys.DEVICELIST);
    if (deviceList.isSAMSUNG) return DeviceTypes.SAMSUNG;
    if (deviceList.isTIZEN_TEP) return DeviceTypes.TIZEN_TEP;
    if (deviceList.isTIZEN && shouldUseTizenTEPPlayer()) return DeviceTypes.TIZEN_TEP;
    if (deviceList.isTIZEN) return DeviceTypes.TIZEN;
    if (deviceList.isWEBOS) return DeviceTypes.WEBOS;
    if (deviceList.isANDROID) return DeviceTypes.ANDROID;
    return DeviceTypes.DEFAULT;
  };

  /**
   * Check if device supports fullscreen toggle
   */
  const supportsFullScreenToggle = () => {
    const deviceType = getDeviceType();
    return [DeviceTypes.SAMSUNG, DeviceTypes.TIZEN, DeviceTypes.TIZEN_TEP].includes(deviceType);
  };

  /**
   * Check if device always streams fullscreen (like webOS)
   */
  const isAlwaysFullscreen = () => {
    const deviceType = getDeviceType();
    return deviceType === DeviceTypes.WEBOS;
  };

  /**
   * Initialize player for specific device
   * Returns device-specific initialization config
   */
  const getPlayerInitConfig = () => {
    const deviceType = getDeviceType();

    switch (deviceType) {
      case DeviceTypes.SAMSUNG:
        return {
          device: DeviceTypes.SAMSUNG,
          containerElement: 'player_container',
          hasToggleFullscreen: true,
        };

      case DeviceTypes.TIZEN:
      case DeviceTypes.TIZEN_TEP:
        return {
          device: deviceType,
          containerElement: 'av-player',
          hasToggleFullscreen: true,
          playerElement: document.getElementById('av-player'),
        };

      case DeviceTypes.WEBOS:
        return {
          device: DeviceTypes.WEBOS,
          containerElement: 'webos-media-container',
          hasToggleFullscreen: false, // webOS is always fullscreen
          alwaysFullscreen: true,
        };

      case DeviceTypes.ANDROID:
        return {
          device: DeviceTypes.ANDROID,
          hasToggleFullscreen: false,
          nativePlayer: true,
        };

      default:
        return {
          device: DeviceTypes.DEFAULT,
          hasToggleFullscreen: false,
        };
    }
  };

  /**
   * Calculate player display coordinates
   * @param {boolean} isFullScreen - Is fullscreen mode
   * @param {string} deviceTypeOverride - Optional device type override
   */
  const getDisplayCoords = (isFullScreen, deviceTypeOverride = null) => {
    const device = deviceTypeOverride || getDeviceType();

    if (isFullScreen) {
      return { x: 0, y: 0, width: window.innerWidth, height: window.innerHeight };
    }

    // Device-specific windowed coords
    switch (device) {
      case DeviceTypes.TIZEN:
      case DeviceTypes.TIZEN_TEP:
        return { x: 500, y: 300, width: 854, height: 480 };
      default:
        return {
          top: 150,
          left: Math.round(window.innerWidth / 3),
          width: 600,
          height: 400,
        };
    }
  };

  /**
   * Reset player state between channel changes
   */
  const resetPlayerStateIfNeeded = (callback) => {
    const playerState = getPlayerState();
    if (!playerState.gAVplayObj) return;

    try {
      const currentState = playerState.gAVplayObj.getState?.();

      if (currentState && currentState !== 'NONE' && currentState !== 'IDLE') {
        playerState.gAVplayObj.stop?.();
        playerState.gAVplayObj.close?.();
      }
    } catch (e) {
      console.warn('Error resetting player state:', e);
    }

    callback?.();
  };

  return {
    getDeviceType,
    supportsFullScreenToggle,
    isAlwaysFullscreen,
    getPlayerInitConfig,
    getDisplayCoords,
    resetPlayerStateIfNeeded,
  };
};

